import { useMemo } from 'react';

// styles
import styles from './filter.module.scss';

// types
import type { FilterProps } from './filter.types';

// components
import { Autocomplete } from './autocomplete';
import DistanceFilter from './distance-filter';
import CategoryFilter from './category-filter';
import SeriesFilter from './series-filter';
import ModelFilter from './model-filter';

// utils
import { classNameBuilder } from 'utils/classNameBuilder';
import { isEmpty } from 'utils/is-empty';

function Filter({
  config,
  mode,
  setMaxDistance,
  setIsSelectedDropdown,
  setCurrentCoords,
  setGeocoded,
  setFilterCategory,
  setFilterModel,
  setFilterSerie,
  countryCode,
  searchPlaceholder = '',
  maxDistance = 'none',
  currentCoords,
  googleMapsRef = null,
  fullData = [],
  seriesLabel,
  productCategoryLabel,
  filterCategory,
  filterModel = [],
  filterSerie,
  hideFilters = false,
  sortedF1Categories = [],
  isMobile = false,
}: Readonly<FilterProps>) {
  const categories = useMemo<string[] | null>(() => {
    return mode === 'showroom' && !isEmpty(fullData)
      ? fullData.reduce((acc: string[], cur: any) => {
          if (cur.products) {
            let listCategories = cur.products;

            if (!isEmpty(filterSerie)) {
              listCategories = listCategories.filter((item: any) =>
                item.series.some((seriesItem: string) => seriesItem === filterSerie),
              );
            }

            listCategories = listCategories.reduce((cats: string[], item: any) => {
              cats.push(item.category);
              return cats;
            }, []);
            listCategories.forEach((cat) => {
              if (!acc.includes(cat)) acc.push(cat);
            });
          }
          return acc;
        }, [])
      : null;
  }, [mode, fullData, filterSerie]);

  const series = useMemo<string[] | null>(() => {
    return mode === 'showroom' && !isEmpty(fullData)
      ? fullData.reduce<string[]>((acc, cur) => {
          if (cur.products) {
            let listSeries: typeof cur.products | string[] = cur.products;

            if (!isEmpty(filterCategory)) {
              listSeries = listSeries.filter((item) => item.category === filterCategory);
            }

            listSeries = listSeries.reduce<string[]>(
              (seriesAcc, item) => seriesAcc.concat(item.series),
              [],
            );
            listSeries.forEach((cat) => {
              if (!acc.includes(cat)) acc.push(cat);
            });
          }
          return acc;
        }, [])
      : null;
  }, [mode, fullData, filterCategory]);

  const models = useMemo<string[] | null>(() => {
    return mode === 'hotel' && !isEmpty(fullData)
      ? fullData.reduce<string[]>((acc, cur) => {
          let newProducts: string[] = [];

          if (cur.models) {
            newProducts = cur.models.map((product) => product.product);
          }

          // avoid duplicates
          newProducts.forEach((product) => {
            if (!acc.includes(product)) {
              acc.push(product);
            }
          });

          return acc;
        }, [])
      : null;
  }, [mode, fullData]);

  return (
    <div className={classNameBuilder(styles.filterWrapper, styles.nord)}>
      <div className={classNameBuilder(styles.filterBar, styles.nord)}>
        <div className={styles.regularFilters}>
          <Autocomplete
            coordinates={currentCoords}
            googleMapsRef={googleMapsRef}
            country={countryCode}
            searchPlaceholder={searchPlaceholder}
            setCurrentCoords={setCurrentCoords}
            setMaxDistance={setMaxDistance}
            maxDistance={maxDistance}
            setGeocoded={setGeocoded}
            mode={mode}
          />
          <DistanceFilter
            maxDistance={maxDistance}
            setMaxDistance={setMaxDistance}
            distanceLabel={config.label}
            setIsSelectedDropdown={setIsSelectedDropdown}
          />
        </div>
        {mode === 'showroom' && !hideFilters && (
          <div className={styles.showroomFilters}>
            <CategoryFilter
              filterCategories={categories}
              filterCategory={filterCategory}
              setFilterCategory={setFilterCategory}
              label={productCategoryLabel}
              sortedF1Categories={sortedF1Categories}
              isMobile={isMobile}
            />
            <SeriesFilter
              filterSeries={series}
              filterSerie={filterSerie}
              setFilterSeries={setFilterSerie}
              label={seriesLabel}
              isMobile={isMobile}
            />
          </div>
        )}
        {mode === 'hotel' && (
          <div className={styles.hotelFilters}>
            <ModelFilter
              label={config.modelLabel ?? ''}
              filterModel={filterModel}
              setFilterModel={setFilterModel}
              models={models}
            />
          </div>
        )}
      </div>
    </div>
  );
}

export default Filter;
